import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import {
  type ActionDefinition,
  OneDataCollection,
  useDataCollectionSource,
} from '@factorialco/f0-react/dist/experimental'
import { Add, Archive } from '@factorialco/f0-react/icons/app'
import { AvailableModules } from 'generated/featureDefinitions'

import { useModuleAvailability } from 'modules/core/hooks/useModuleAvailability'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useArchiveWorkArea } from 'modules/locations/hooks/useArchiveWorkArea'
import { useUnArchiveWorkArea } from 'modules/locations/hooks/useUnArchiveWorkArea'
import { WorkAreaNode } from 'modules/locations/hooks/useWorkAreas'

type WorkArea = NonNullable<WorkAreaNode>

interface Props {
  workAreas: WorkArea[]
  locationId: string
  canManageWorkArea: boolean
  canAddSpace: boolean
}

const WorkAreasDataCollection = ({
  workAreas,
  locationId,
  canManageWorkArea,
  canAddSpace,
}: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { archiveWorkArea } = useArchiveWorkArea()
  const { unArchiveWorkArea } = useUnArchiveWorkArea()
  const { moduleAvailability } = useModuleAvailability(AvailableModules.space_control)
  const spacesEnabled = moduleAvailability.available

  const source = useDataCollectionSource<WorkArea>(
    {
      dataAdapter: {
        fetchData: (options) => {
          let filtered = [...workAreas]

          // Client-side search (BR-010)
          if (options.search) {
            const search = options.search.toLowerCase()
            filtered = filtered.filter((wa) => wa.name.toLowerCase().includes(search))
          }

          // Archived filter (BR-011, BR-013): default is only non-archived
          const archivedFilter = options.filters?.archived
          if (Array.isArray(archivedFilter) && archivedFilter.includes('archived')) {
            // Show all (archived + non-archived)
          } else {
            filtered = filtered.filter((wa) => !wa.archivedAt)
          }

          return { records: filtered }
        },
      },
      search: { enabled: true },
      filters: {
        archived: {
          type: 'in' as const,
          label: i18n.t('settings.locations.location.work_areas.archived.filter'),
          options: {
            options: [
              {
                label: i18n.t('settings.locations.location.work_areas.archived.show'),
                value: 'archived',
              },
            ],
          },
        },
      },
      ...(canManageWorkArea
        ? {
            primaryActions: () => ({
              label: i18n.t('settings.locations.location.work_areas.add.button'),
              icon: Add,
              onClick: () => {
                track(trackingEvents.workAreas.addWorkAreaStart, {
                  event_category: 'Workplaces',
                  location_id: locationId,
                })
                navigate(
                  navigation.settings.locations.locationsId.workAreas.add.fullPath({
                    id: locationId,
                  })
                )
              },
            }),
          }
        : {}),
      itemActions: (workArea: WorkArea) => {
        const isArchived = !!workArea.archivedAt
        const workSpacesCount = workArea.workSpacesConnection?.totalCount ?? 0
        const canManageSpaces = spacesEnabled && canAddSpace

        const actions: ActionDefinition[] = []

        // Edit work area — secondary action (card footer button)
        if (canManageWorkArea) {
          actions.push({
            label: i18n.t('settings.locations.location.work_areas.edit.title'),
            type: 'secondary',
            onClick: () => {
              track(trackingEvents.workAreas.editWorkAreaStart, {
                event_category: 'Workplaces',
                location_id: locationId,
                workarea_id: workArea.id,
              })
              navigate({
                pathname:
                  navigation.settings.locations.locationsId.workAreas.workAreaIdEdit.fullPath({
                    id: locationId,
                    workAreaId: workArea.id,
                  }),
              })
            },
          })
        }

        // Manage spaces — in dropdown
        if (canManageSpaces) {
          actions.push({
            label: i18n.t('settings.locations.location.work_areas.manage_spaces'),
            type: 'other',
            onClick: () => {
              track(trackingEvents.spaceControl.seeOverview, {})
              navigate(
                navigation.settings.locations.locationsId.workAreas.workSpaces.fullPath({
                  id: locationId,
                  workAreaId: workArea.id,
                })
              )
            },
          })
        }

        // View floor map / Upload floor map — in dropdown (BR-006)
        const mapUrl = workArea.workAreaMap?.image?.url
        if (mapUrl) {
          actions.push({
            label: i18n.t('settings.locations.location.work_areas.map.has_map'),
            type: 'other',
            onClick: () => {
              window.open(mapUrl, '_blank', 'noopener,noreferrer')
            },
          })
        } else if (canManageWorkArea) {
          actions.push({
            label: i18n.t('settings.locations.location.work_areas.map.upload_floor_map'),
            type: 'other',
            onClick: () => {
              navigate({
                pathname:
                  navigation.settings.locations.locationsId.workAreas.workAreaIdEdit.fullPath({
                    id: locationId,
                    workAreaId: workArea.id,
                  }),
              })
            },
          })
        }

        if (canManageWorkArea) {
          // Separator before destructive action
          actions.push({ type: 'separator' })

          if (isArchived) {
            // Unarchive (BR-012)
            actions.push({
              label: i18n.t('common.unarchive'),
              type: 'other',
              onClick: () => {
                unArchiveWorkArea({ id: workArea.id })
              },
            })
          } else {
            // Archive (disabled when spaces exist — ERR-001)
            actions.push({
              label: i18n.t('settings.locations.location.work_areas.archive'),
              critical: true,
              type: 'other',
              enabled: workSpacesCount === 0,
              onClick: () => {
                track(trackingEvents.workAreas.deleteWorkAreaStart, {
                  event_category: 'Workplaces',
                  workarea_id: workArea.id,
                  location_id: locationId,
                })
                archiveWorkArea({ id: workArea.id })
                track(trackingEvents.workAreas.deleteWorkAreaSubmit, {
                  event_category: 'Workplaces',
                  workarea_id: workArea.id,
                  location_id: locationId,
                })
              },
            })
          }
        }

        return actions
      },
    },
    [
      workAreas,
      locationId,
      canManageWorkArea,
      canAddSpace,
      spacesEnabled,
      archiveWorkArea,
      unArchiveWorkArea,
    ]
  )

  return (
    <OneDataCollection
      fullHeight
      source={source}
      emptyStates={{
        'no-data': {
          title: i18n.t('settings.locations.location.work_areas.title'),
          description: i18n.t('settings.locations.location.work_areas.empty_message'),
        },
      }}
      visualizations={[
        {
          type: 'card',
          options: {
            title: (workArea: WorkArea) => workArea.name,
            compact: true,
            description: (workArea: WorkArea) => {
              if (!spacesEnabled) return ''
              const count = workArea.workSpacesConnection?.totalCount ?? 0
              return count === 1
                ? i18n.t('settings.locations.location.work_areas.spaces_count.one', { count })
                : i18n.t('settings.locations.location.work_areas.spaces_count.other', { count })
            },
            cardProperties: [
              {
                label: '',
                icon: Archive,
                render: (workArea: WorkArea) => {
                  if (!workArea.archivedAt) return undefined
                  return {
                    type: 'status' as const,
                    value: {
                      status: 'neutral',
                      label: i18n.t('settings.locations.location.work_areas.archived.filter'),
                    },
                  }
                },
              },
            ],
          },
        },
      ]}
    />
  )
}

export default WorkAreasDataCollection
